<template>
    <button
        type="button"
        :disabled="!isInteractive"
        :aria-hidden="isPlaceholder"
        :class="[
            'group flex flex-col items-center justify-center py-2 text-xs transition focus:outline-none',
            showLabel ? 'gap-1 px-1' : 'gap-0.5 px-0.5',
            isInteractive ? 'cursor-pointer' : 'cursor-default',
            isPlaceholder ? 'invisible' : '',
        ]"
        :title="isPlaceholder ? '' : cellTitle"
        :aria-label="isPlaceholder ? '' : cellTitle"
        @click.stop="handleClick"
    >
        <span
            :class="[
                'inline-flex shrink-0 items-center justify-center rounded-full border text-white transition',
                showLabel ? 'h-6 w-6' : 'h-4 w-4',
                ringClass,
                backgroundClass,
                isInteractive ? 'group-hover:scale-110 group-focus:ring-2 group-focus:ring-primary group-focus:ring-offset-2' : '',
            ]"
        >
            <Check v-if="cell.status === 'performed'" :class="showLabel ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'" />
            <Hourglass v-else-if="cell.status === 'scheduled'" :class="showLabel ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'" />
            <Cross v-else-if="cell.status === 'not_performed'" :class="showLabel ? 'h-3 w-3' : 'h-2 w-2'" />
        </span>
        <span v-if="showLabel" class="text-[11px] text-muted">{{ periodLabel }}</span>
        <span
            v-if="showLabel"
            class="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-primary"
            :class="isInteractive ? '' : 'invisible'"
            :title="isInteractive ? trans('dashboard.v2.preventative_maintenance.cell.work_order') : ''"
            :aria-hidden="!isInteractive"
            :aria-label="isInteractive ? trans('dashboard.v2.preventative_maintenance.cell.work_order') : ''"
        >
            <File class="h-3 w-3" />
        </span>
    </button>
</template>

<script setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import Check from '@/../icons/Check.vue';
import Cross from '@/../icons/Cross.vue';
import File from '@/../icons/File.vue';
import Hourglass from '@/../icons/Hourglass.vue';
import { trans } from '@/mixins/i18n';
import { loadWorkOrderDetails } from '@/Pages/WorkOrders/loadWorkOrderDetails';
import { monthLabel } from './months';

const props = defineProps({
    cell: {
        type: Object,
        required: true,
    },
    /**
     * Whether to render the month label beneath the dot. Suppressed for dense
     * cadences (e.g. monthly) where labels would collide; the month stays
     * available via the dot's tooltip (see cellTitle).
     */
    showLabel: {
        type: Boolean,
        default: true,
    },
});

const backgroundClass = computed(() => {
    switch (props.cell.status) {
        case 'performed':
            return 'bg-compliance-approved border-compliance-approved';
        case 'scheduled':
            return 'bg-pm-scheduled border-pm-scheduled';
        case 'not_performed':
            return 'bg-compliance-inoperable border-compliance-inoperable';
        case 'planned':
        default:
            return 'bg-compliance-unscheduled border-gray-300';
    }
});

const ringClass = computed(() => (props.cell.status === 'planned' ? 'border-2' : 'border'));

const isPlaceholder = computed(() => props.cell.status === 'placeholder');

const isInteractive = computed(() => Boolean(props.cell.workOrderId));

const page = usePage();
const periodLabel = computed(() => monthLabel(props.cell.periodStart, page.props.locale ?? 'en'));

const ariaLabel = computed(() => trans(`dashboard.v2.preventative_maintenance.status.${props.cell.status}`));

// Always carry the month in the tooltip/aria-label (status + month) so the
// period stays discoverable on hover even when the visible label is dropped
// for dense cadences.
const cellTitle = computed(() => (periodLabel.value ? `${periodLabel.value} · ${ariaLabel.value}` : ariaLabel.value));

const handleClick = () => {
    if (props.cell.workOrderId) {
        loadWorkOrderDetails(props.cell.workOrderId);
    }
};
</script>
